import React, { useEffect } from "react";

interface WaitlistConfirmationModalProps {
  isOpen: boolean;
  // Public waitlist position from the server, for new joins and for people
  // already on the list. Null only if the response omitted it.
  position: number | null;
  alreadyOnWaitlist?: boolean;
  onClose: () => void;
  getAppUrl: string;
  whatsappUrl: string;
  telegramUrl: string;
  twitterUrl: string;
}

const WaitlistConfirmationModal: React.FC<WaitlistConfirmationModalProps> = ({
  isOpen,
  position,
  alreadyOnWaitlist,
  onClose,
  getAppUrl,
  whatsappUrl,
  telegramUrl,
  twitterUrl,
}) => {
  useEffect(() => {
    if (isOpen) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-black border border-[#3a3a3a] rounded-[36px] w-full max-w-[660px] flex flex-col items-center gap-9 p-12"
      >
        {/* Title */}
        <h2 className="text-white text-center font-bold text-[36px] leading-[1.2]">
          {position === null
            ? "You're on the waitlist"
            : alreadyOnWaitlist
              ? `You're already #${position} on the list`
              : `You're #${position} on the list`}
        </h2>

        {/* Subtitle */}
        <p className="text-center font-normal text-[23px] text-[#EFEFEF] leading-[1.44]">
          Can't wait? V1 is live.
        </p>

        {/* Buttons Container */}
        <div className="flex flex-col gap-[18px] w-full">
          {/* Get the Bitoshi App Button */}
          <a
            href={getAppUrl}
            target="_blank"
            rel="noreferrer"
            className="w-full bg-white border border-[#4B4B4B] text-black h-[72px] rounded-full transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 cursor-pointer"
          >
            <div className="flex flex-row gap-1">
              <img
                src="/images/waitlist-modal/appstore-icon.svg"
                alt="App Store"
                className="h-[32px] w-auto"
              />
              <img
                src="/images/waitlist-modal/playstore-icon.svg"
                alt="Google Play"
                className="h-[32px] w-auto"
              />
            </div>
            <span className="flex flex-col items-start leading-none">
              <span className="font-light text-[24px]">Get the</span>
              <span className="font-bold text-[24px]">Bitoshi App</span>
            </span>
          </a>

          {/* WhatsApp Button */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
            className="w-full bg-[#25D366] border border-[#25D366] text-black font-semibold text-[23px] h-[72px] rounded-full transition-all transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer flex items-center justify-center text-center"
          >
            Join our WhatsApp community
          </a>

          {/* Telegram Button */}
          <a
            href={telegramUrl}
            target="_blank"
            rel="noreferrer"
            className="w-full bg-[#353535] border border-[#4B4B4B] text-white font-medium text-[23px] h-[72px] rounded-full transition-all transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer flex items-center justify-center text-center"
          >
            Join our Telegram channel
          </a>

          {/* Twitter Button */}
          <a
            href={twitterUrl}
            target="_blank"
            rel="noreferrer"
            className="w-full bg-[#353535] border border-[#4B4B4B] text-white font-medium text-[23px] h-[72px] rounded-full transition-all transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
          >
            <span className="flex items-center justify-center h-full">
              Follow us on X
            </span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default WaitlistConfirmationModal;
